import { useCallback, useEffect, useRef } from 'react'
import {
  TRoomViewSettingsPopupProps,
  TRoomViewSettingsState,
} from './RomViewSettingsPopup.types'
import { useIsMobile } from 'lib/utils/useIsMobile'
import { useRoomViewStore } from 'store/roomView/roomView.store'
import { useUpdateAppConfig } from '../hooks/appConfig.hook'
import { captureWarning } from 'lib/monitoring'
import { enqueueSnackbar } from 'notistack'

export const useSettingsPopupLogic = (props: TRoomViewSettingsPopupProps) => {
  const { isOpen, onClose } = props
  const [updateAppConfig] = useUpdateAppConfig()
  const { roomViewSettings, setRoomViewSettings } = useRoomViewStore()

  const isMobile = useIsMobile()
  const popupRef = useRef<HTMLDivElement>(null)

  const handleSettingToggle = useCallback(
    async (settingKey: keyof TRoomViewSettingsState, value: boolean) => {
      const cleanCurrentSettings = {
        showProcedureName: roomViewSettings.showProcedureName,
        showStaffAssignment: roomViewSettings.showStaffAssignment,
        showExpectedEndTime: roomViewSettings.showExpectedEndTime,
      }

      const newSettings = {
        ...cleanCurrentSettings,
        [settingKey]: value,
      }
      setRoomViewSettings(newSettings)

      try {
        await updateAppConfig({
          variables: {
            input: {
              roomViewSettings: {
                [settingKey]: value,
              },
            },
          },
        })
      } catch (error) {
        setRoomViewSettings(cleanCurrentSettings)
        if (error instanceof Error) {
          captureWarning(error)
        }
        enqueueSnackbar('Failed to update settings', {
          variant: 'error',
        })
      }
    },
    [roomViewSettings, setRoomViewSettings, updateAppConfig]
  )

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (
      popupRef.current &&
      e.target instanceof Node &&
      !popupRef.current.contains(e.target)
    ) {
      onClose()
    }
  }

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popupRef.current &&
        event.target instanceof Node &&
        !popupRef.current.contains(event.target)
      ) {
        const target = event.target instanceof Element ? event.target : null
        if (target) {
          const settingsButton =
            target.closest('[data-testid="settings-button"]') ||
            target.closest('button[aria-label="Settings"]') ||
            target
              .closest('button')
              ?.querySelector('svg[data-testid="SettingsIcon"]')
              ?.closest('button')
          if (!settingsButton) {
            onClose()
          }
        }
      }
    }

    if (isOpen) {
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timer)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen, onClose])

  return {
    isMobile,
    popupRef,
    roomViewSettings,
    handleBackdropClick,
    handleSettingToggle,
  }
}
